import { useEffect, useMemo, useState } from 'react';
import type { ExperienceProfile, ExperienceProfileInput } from '@nava/backoffice-bff-contracts';
import {
  experienceLocales,
  experienceTextKeys,
  getExperienceDefaults,
  resolveExperienceCopy,
  validateExperienceOverrides,
} from '@nava/participant-experience';

const areas = [...new Set(experienceTextKeys.map((key) => key.split('.')[0]))].sort();
const formatArea = (area: string) =>
  area.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (value) => value.toUpperCase());

interface Props {
  profile?: ExperienceProfile;
  busy: boolean;
  onSave: (input: ExperienceProfileInput) => void;
  onCancel: () => void;
  onDirtyChange?: (dirty: boolean) => void;
}
export function ExperienceProfileEditor({ profile, busy, onSave, onCancel, onDirtyChange }: Props) {
  const [name, setName] = useState(profile?.name ?? '');
  const [description, setDescription] = useState(profile?.description ?? '');
  const [overrides, setOverrides] = useState<ExperienceProfileInput['overrides']>(
    structuredClone(profile?.overrides ?? {})
  );
  const dirty =
    name !== (profile?.name ?? '') ||
    description !== (profile?.description ?? '') ||
    JSON.stringify(overrides) !== JSON.stringify(profile?.overrides ?? {});
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);
  const [line, setLine] = useState<'insights' | 'customer_success'>('insights');
  const [locale, setLocale] = useState('en');
  const [search, setSearch] = useState('');
  const [area, setArea] = useState('');
  const [error, setError] = useState('');
  const defaults = useMemo(() => getExperienceDefaults(line, locale), [line, locale]);
  const effective = useMemo(
    () => resolveExperienceCopy(line, locale, overrides),
    [line, locale, overrides]
  );
  const locales = [
    ...new Set([
      ...experienceLocales,
      ...Object.keys(overrides.insights ?? {}),
      ...Object.keys(overrides.customer_success ?? {}),
    ]),
  ].sort();
  const setText = (key: string, value: string) =>
    setOverrides((current) => {
      const next = structuredClone(current);
      const texts = { ...next[line]?.[locale] };
      if (value.trim()) texts[key] = value;
      else delete texts[key];
      next[line] = { ...next[line], [locale]: texts };
      return next;
    });
  const keys = experienceTextKeys.filter(
    (key) =>
      (!area || key.split('.')[0] === area) &&
      `${key} ${defaults[key]}`.toLowerCase().includes(search.toLowerCase())
  );
  return (
    <form
      className="space-y-5 rounded-xl border bg-white p-6"
      onSubmit={(event) => {
        event.preventDefault();
        try {
          validateExperienceOverrides(overrides);
          if (!name.trim()) throw new Error('Enter a profile name');
          setError('');
          onSave({ name: name.trim(), description: description.trim(), overrides });
        } catch (e) {
          setError((e as Error).message);
        }
      }}
    >
      <fieldset disabled={busy} className="space-y-5">
        <h2 className="text-xl font-semibold">{profile ? 'Edit profile' : 'Create profile'}</h2>
        <label className="block">
          Name
          <input
            required
            maxLength={150}
            className="mt-1 block w-full rounded border p-2"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label className="block">
          Description
          <textarea
            maxLength={1000}
            className="mt-1 block w-full rounded border p-2"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>
        <div className="flex flex-wrap gap-4">
          <label>
            Product line
            <select
              className="ml-2 rounded border p-2"
              value={line}
              onChange={(e) => setLine(e.target.value as typeof line)}
            >
              <option value="insights">Insights</option>
              <option value="customer_success">Customer Success</option>
            </select>
          </label>
          <label>
            Language
            <select
              className="ml-2 rounded border p-2"
              value={locale}
              onChange={(e) => setLocale(e.target.value)}
            >
              {locales.map((code) => (
                <option key={code}>{code}</option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="rounded border px-3"
            onClick={() =>
              setOverrides((current) => ({
                ...current,
                [line === 'insights' ? 'customer_success' : 'insights']: {
                  ...current[line === 'insights' ? 'customer_success' : 'insights'],
                  [locale]: { ...current[line]?.[locale] },
                },
              }))
            }
          >
            Copy overrides to other product line
          </button>
        </div>
        <p className="text-sm text-neutral-600">
          Leave a field empty to inherit the product default. Saved changes apply to previews;
          publish a study to update its live wording.
        </p>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
          <label className="block min-w-0 flex-1">
            Find text
            <input
              className="mt-1 block w-full rounded border p-2"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          <label className="block sm:w-48">
            Areas
            <select
              className="mt-1 block w-full rounded border p-2"
              value={area}
              onChange={(e) => setArea(e.target.value)}
            >
              <option value="">All areas</option>
              {areas.map((value) => (
                <option key={value} value={value}>
                  {formatArea(value)}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="max-h-[60vh] space-y-5 overflow-y-auto">
          {keys.map((key) => (
            <div key={key} className="rounded border p-4">
              <label htmlFor={`copy-${key}`} className="font-medium">
                {key
                  .split('.')
                  .map((part) =>
                    part
                      .replace(/([a-z])([A-Z])/g, '$1 $2')
                      .replace(/^./, (value) => value.toUpperCase())
                  )
                  .join(' · ')}
              </label>
              <p className="my-2 whitespace-pre-wrap text-sm text-neutral-600">
                Product default: {defaults[key]}
              </p>
              <textarea
                id={`copy-${key}`}
                maxLength={4000}
                rows={3}
                className="block w-full rounded border p-2"
                placeholder={defaults[key]}
                value={overrides[line]?.[locale]?.[key] ?? ''}
                onChange={(e) => setText(key, e.target.value)}
              />
              <div className="mt-2 flex justify-between gap-3 text-sm">
                <span>
                  {overrides[line]?.[locale]?.[key] ? 'Profile override' : 'Inherited'} · Effective:{' '}
                  {effective[key]}
                </span>
                <button
                  type="button"
                  className="shrink-0 underline"
                  onClick={() => setText(key, '')}
                >
                  Reset to default
                </button>
              </div>
            </div>
          ))}
        </div>
        {error && (
          <p role="alert" className="text-red-700">
            {error}
          </p>
        )}
        <div className="flex gap-3">
          <button disabled={busy} className="rounded bg-violet-700 px-4 py-2 text-white">
            {busy ? 'Saving...' : 'Save profile'}
          </button>
          <button type="button" onClick={onCancel} className="rounded border px-4 py-2">
            Cancel
          </button>
        </div>
      </fieldset>
    </form>
  );
}
